import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  OpoContactForm,
  UPDATE_OPO_CONTACT_FORM_DEFAULT_VALUES,
  updateOpoContactFormSchema,
  UpdateOpoContactFormValues,
} from '@lp/features/admin/forms/OpoContactForm';
import { useAuthContext } from '@lp/core/auth';
import { OpoContact } from '@/api/apis/opo';
import { retrieveUnifiedError } from '@/api/utils';
import { FileType } from '@/queries/file';
import { useUpdateOpoContactMutation, useUpdateOpoUserMutation } from '@/queries/opo';
import { useWrappedRemoveOpoContactMutation } from '@/queries/orchestrator';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useUploadFile } from '@/hooks/useUploadFile';
import { AdminPermissions } from '@/permissions';
import { getChangedValues } from '@/utils/helpers';

export const OpoContactEdit = () => {
  const navigate = useNavigate();
  const { uploadFiles } = useUploadFile(FileType.Opo);
  const { checkPermission } = useAuthContext();
  const hasCreatePermissions = checkPermission(AdminPermissions.OpoCreate);
  const data = useLoaderData<OpoContact>();
  const { mutateAsync: mutateOpoContact } = useUpdateOpoContactMutation();
  const { mutateAsync: mutateOpoUser } = useUpdateOpoUserMutation();

  const defaultValuesOpo = useMemo<UpdateOpoContactFormValues>(
    () => ({
      ...UPDATE_OPO_CONTACT_FORM_DEFAULT_VALUES,
      isOpoUser: data.isOpoUser || false,
      userId: data.userId || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      photoPath: data.photoPath ?? null,
      primaryPhone: data.primaryPhone || '',
      secondaryPhone: data.secondaryPhone || '',
      isActive: data.isActive ?? true,
      inviteSent: data.inviteSent ?? false,
      memberships: (data.organizations ?? []).map((org) => ({
        opoOrganizationId: org.id,
        opoOrganization: {
          id: org.id,
          name: org.name,
          code: org.code,
          location: org.location,
          createdAt: org.createdAt,
          modifiedAt: org.modifiedAt,
          deletedAt: org.deletedAt,
        },
        roles: org.roles,
      })),
    }),
    [data],
  );

  const handleBackToList = () => {
    navigate(`/admin/opos/contacts`);
  };

  const handleSubmit = async (values: UpdateOpoContactFormValues) => {
    try {
      let uploadedFile;
      if (values.photo) {
        uploadedFile = await uploadFiles([values.photo]);
      }

      const requestBody = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        primaryPhone: values.primaryPhone,
        secondaryPhone: values.secondaryPhone,
        photoPath: uploadedFile ? uploadedFile[0].fileName : (values.photoPath ?? null),
        isActive: values.isActive,
        memberships: values.memberships.map(({ opoOrganizationId, roles }) => ({ opoOrganizationId, roles })),
      };

      const changedValues = getChangedValues<UpdateOpoContactFormValues>(requestBody, defaultValuesOpo);

      if (values.inviteAsUser && values.email && values.firstName && values.lastName) {
        await mutateOpoUser({ id: data.id, ...changedValues });
      } else {
        await mutateOpoContact({ id: data.id, ...changedValues });
      }

      toast.success('OPO Contact updated successfully.');
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const {
    isOpen: isOpoDeleteOpen,
    isLoading: isOpoDeleteLoading,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveOpoContactMutation,
    successMessage: 'OPO Contact deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  return (
    <>
      <Form<UpdateOpoContactFormValues>
        defaultValues={defaultValuesOpo}
        schema={updateOpoContactFormSchema}
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit OPO Contact" backToListLink="/admin/opos/contacts">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={() => onModalOpen(data.id)}
              hasEditPermissions={hasCreatePermissions}
              hasDeletePermissions={hasCreatePermissions}
            />
          </AdminPageHeader>
          <OpoContactForm isReadOnly={!hasCreatePermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete OPO Contact?"
        description="Are you sure you want to delete the OPO Contact?"
        size="lg"
        isOpen={isOpoDeleteOpen}
        isLoading={isOpoDeleteLoading}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
